<template>
	<li
		v-show="isVisible"
		ref="OptionRef"
		class="kk-select-option"
		:class="{
			'kk-select-option--selected': isSelected,
			'kk-select-option--disabled': isDisabled,
			'kk-select-option--grouped': isGrouped,
		}"
		@click.stop="onClick"
	>
		<div class="kk-select-option__icon-text">
			<slot name="leading-image">
				<KkSelectLeadingImage
					v-if="leadingImage"
					class="kk-select-option__leading-image"
					:config="leadingImage"
					:is-disabled="isDisabled"
				/>
			</slot>
			<div class="kk-select-option__content">
				<slot v-bind="scopedSlots">
					{{ label }}
				</slot>
			</div>
		</div>
		<KkIcon
			v-if="showTrailingIcon"
			class="kk-select__trailing-icon"
			:name="showTrailingIcon"
			size="sm"
		/>
	</li>
</template>

<script>
import KkIcon from '../../KkIcon';
import KkSelectLeadingImage from '../../KkSelectLeadingImage';
import {
	computed,
	inject,
	watch,
	ref,
	getCurrentInstance,
	nextTick,
} from 'vue';

export default {
	name: 'KkSelectOption',
	components: {
		KkSelectLeadingImage,
		KkIcon
	},
	props: {
		value: {
			required: true,
			type: [String, Number, Boolean],
		},
		label: {
			type: [String, Number],
			default: '',
		},
		isDisabled: Boolean,
		leadingImage: {
			type: [String, Object], // KkIcon 可以直接縮寫傳入，其餘需要依據 config 設定傳入
			default: '',
		},
		trailingIcon: {
			type: String,
			default: '',
		},
	},
	setup(props, { emit }) {
		const OptionRef = ref(null);
		const scopedSlots = ref({});
		const isVisible = ref(true);
		const select = inject('KkSelectWithMenu', {});
		const selectOptionGroup = inject('KkSelectOptionGroup', null);

		const isSelected = computed(() => {
			return props.value === select.value;
		})
		const showTrailingIcon = computed(() => {
			if (isSelected.value) {
				return 'ic_check_line_semibold';
			}
			return props.trailingIcon;
		})

		function handleOptionGroup() {
			if (!selectOptionGroup) {
				return;
			}
			const { pushVisibleChildList, removeVisibleChildList } = selectOptionGroup;
			if (isVisible.value) {
				pushVisibleChildList(props.value);
				return;
			}
			removeVisibleChildList(props.value);
		}
		function onSelect() {
			select?.onOptionSelect?.({
				value: props.value,
				label: props.label,
				el: OptionRef.value,
			});
		}
		function onClick() {
			if (props.isDisabled !== true) {
				onSelect();
				emit('click', props.value)
			}
		}

    function getComputedStyleColor() {
      // Workaround for building in Node environment
      if (typeof window === 'undefined') {
        return '';
      }
      return window.getComputedStyle(OptionRef.value).getPropertyValue('--kk-select-option-color');
    }

		watch([isSelected, () => props.isDisabled], () => {
			nextTick(() => {
				scopedSlots.value = {
					color: getComputedStyleColor(),
				}
			})
		}, { immediate: true });
		watch(isSelected, () => {
			if (isSelected.value) {
				select?.setSelected({
					value: props.value,
					label: props.label,
					el: OptionRef.value,
				});
			}
		}, { immediate: true })
		watch(
			() => select.query,
			() => {
				isVisible.value = select.query ? props.label.includes(select.query) : true;
				handleOptionGroup();
			},
			{ immediate: true },
		)
		select?.addOption?.(getCurrentInstance().proxy);

		return {
			OptionRef,
			showTrailingIcon,
			isSelected,
			isVisible,
			isGrouped: !!selectOptionGroup,
			onClick,
			scopedSlots,
		}
	},
}
</script>
